import { useMemo } from "react";

import type { Asset, Margin } from "~/v2/api/rest/account";
import { getPrettyCash } from "~/v2/utils/formatting";

export interface UseBuyingPowerProps {
  asset: Asset | null | undefined;
  margin: Margin | null | undefined;
  isOvernight?: boolean;
}

export const getBuyingPower = ({
  asset,
  margin,
  isOvernight,
}: UseBuyingPowerProps): { value: string; display: string; label: string } => {
  // multiplier > 1 means a margin account (2 = RegT, 4 = DTBP). A missing or
  // malformed multiplier is treated as cash so we never overstate buying power.
  const isMarginAccount = Number(margin?.multiplier) > 1;

  // Margin buying power only applies to a margin account trading a marginable
  // security. A non-marginable asset (or a cash account) can only deploy
  // non_marginable_buying_power, regardless of session.
  const useMarginBuyingPower = isMarginAccount && Boolean(asset?.marginable);

  const buyingPower =
    (useMarginBuyingPower
      ? isOvernight
        ? margin?.regt_buying_power
        : margin?.effective_buying_power
      : margin?.non_marginable_buying_power) || "0";

  const label =
    useMarginBuyingPower && isOvernight
      ? "Reg T Buying Power"
      : isOvernight
        ? "Cash Buying Power"
        : "Buying Power";

  return {
    value: buyingPower,
    display: getPrettyCash(buyingPower, {
      withPrefix: false,
    }),
    label,
  };
};

export const useBuyingPower = ({
  asset,
  margin,
  isOvernight,
}: UseBuyingPowerProps) => {
  return useMemo(
    () => getBuyingPower({ asset, margin, isOvernight }),
    [asset, margin, isOvernight],
  );
};
